import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../environments/environment';
import {
  SpsAltbestandBericht,
  SpsAltbestandErgebnis,
  SpsAnlagenteilOptionen,
  SpsBereinigungBericht,
  SpsBereinigungErgebnis,
  SpsFortschritt,
  SpsLoeschErgebnis,
  SpsPruefprotokoll,
  SpsPruefstatusRequest,
  SpsSeite,
  SpsSignal,
  SpsSignalDetail,
  SpsSignalFilter,
  SpsSignalLoeschModus,
  SpsSignalLoeschvorschau,
  SpsSignalNachbarn,
  SpsSignaleZusammenfuehrenRequest,
  SpsUngeklaerteZuordnung,
  SpsZuordnungAutomatischErgebnis,
  SpsZuordnungBestaetigenRequest,
  SpsZuordnungEigenstaendigRequest
} from '../Models/Sps';
import { SpsOfflinePaket } from '../Models/offline.model';

/**
 * Gemeinsame SPS-Prüfung und Signalverknüpfung (Backend: SpsPruefungController,
 * Basisroute `/api/sps`).
 *
 * Nebenläufigkeit: jedes Signal trägt eine `version`. Beim Statuswechsel wird
 * sie mitgesendet; hat jemand anderes zwischenzeitlich geprüft, antwortet das
 * Backend mit HTTP 409 – siehe `istVersionskonflikt`.
 */
@Injectable({ providedIn: 'root' })
export class SpsPruefungApiService {
  private readonly baseUrl = environment.apiBaseUrl + '/sps';

  constructor(private http: HttpClient) { }


  // =========================================================
  // SIGNALE
  // =========================================================

  /** Signale eines Projekts – serverseitig gefiltert, sortiert und seitenweise. */
  getSignale(filter: SpsSignalFilter): Observable<SpsSeite<SpsSignal>> {
    return this.http.get<SpsSeite<SpsSignal>>(`${this.baseUrl}/signale`, {
      params: SpsPruefungApiService.filterParams(filter)
    });
  }

  /** Nur Eingänge eines Projekts (Komfort über `getSignale`). */
  getEingaenge(filter: Omit<SpsSignalFilter, 'richtung'>): Observable<SpsSeite<SpsSignal>> {
    return this.getSignale({ ...filter, richtung: 'Eingang' });
  }

  /** Nur Ausgänge eines Projekts (Komfort über `getSignale`). */
  getAusgaenge(filter: Omit<SpsSignalFilter, 'richtung'>): Observable<SpsSeite<SpsSignal>> {
    return this.getSignale({ ...filter, richtung: 'Ausgang' });
  }

  /** Ein Signal mit allen Quellen (Originalzeilen) und den letzten Protokolleinträgen. */
  getSignal(signalId: number): Observable<SpsSignalDetail> {
    return this.http.get<SpsSignalDetail>(`${this.baseUrl}/signale/${signalId}`);
  }

  /**
   * Prüfstatus setzen. Bei `NichtOk` ist die Bemerkung Pflicht (400).
   * `version` mitgeben; bei Abweichung antwortet das Backend mit 409.
   */
  setStatus(signalId: number, request: SpsPruefstatusRequest): Observable<SpsSignal> {
    return this.http.put<SpsSignal>(`${this.baseUrl}/signale/${signalId}/status`, request);
  }

  /** Chronologischer Prüfverlauf (älteste Änderung zuerst). */
  getVerlauf(signalId: number): Observable<SpsPruefprotokoll[]> {
    return this.http.get<SpsPruefprotokoll[]>(`${this.baseUrl}/signale/${signalId}/verlauf`);
  }

  /** Was das Löschen eines Signals bewirken würde: alle Herkunftszeilen, Prüfstatus, Protokolle. Ändert nichts. */
  getLoeschvorschau(signalId: number): Observable<SpsSignalLoeschvorschau> {
    return this.http.get<SpsSignalLoeschvorschau>(`${this.baseUrl}/signale/${signalId}/loeschvorschau`);
  }

  /**
   * Signal löschen (eine Transaktion im Backend).
   * `NurAusPruefung`: Herkunftszeilen bleiben in ihren Listen und werden nicht erneut verknüpft.
   * `Vollstaendig`: Signal, Prüfverlauf und ALLE Herkunftszeilen werden gelöscht.
   */
  deleteSignal(signalId: number, modus: SpsSignalLoeschModus): Observable<SpsLoeschErgebnis> {
    const params = new HttpParams().set('modus', modus);
    return this.http.delete<SpsLoeschErgebnis>(`${this.baseUrl}/signale/${signalId}`, { params });
  }

  /**
   * Vorheriges und nächstes Signal in derselben Filterung und Sortierung wie
   * `getSignale` (Seite und Größe spielen keine Rolle) – für die Navigation
   * in der Signaldetailansicht.
   */
  getNachbarn(signalId: number, filter: SpsSignalFilter): Observable<SpsSignalNachbarn> {
    return this.http.get<SpsSignalNachbarn>(`${this.baseUrl}/signale/${signalId}/nachbarn`, {
      params: SpsPruefungApiService.filterParams(filter)
    });
  }

  /** Query-Parameter eines Signalfilters – für `getSignale`, `getNachbarn` und die URL der Prüfung. */
  static filterParams(filter: SpsSignalFilter): HttpParams {
    let params = new HttpParams()
      .set('projektId', filter.projektId)
      .set('seite', filter.seite ?? 1)
      .set('groesse', filter.groesse ?? 50);

    if (filter.richtung) { params = params.set('richtung', filter.richtung); }
    if (filter.status) { params = params.set('status', filter.status); }
    if (filter.suche) { params = params.set('suche', filter.suche); }
    if (filter.name) { params = params.set('name', filter.name); }
    if (filter.adresse) { params = params.set('adresse', filter.adresse); }
    if (filter.bmk) { params = params.set('bmk', filter.bmk); }
    if (filter.beschreibung) { params = params.set('beschreibung', filter.beschreibung); }
    if (filter.station) { params = params.set('station', filter.station); }
    if (filter.ohneAnlagenteil) {
      params = params.set('ohneAnlagenteil', 'true');
    } else if (filter.anlagenteilId != null) {
      params = params.set('anlagenteilId', filter.anlagenteilId);
    }
    if (filter.nurUngeklaert) { params = params.set('nurUngeklaert', 'true'); }
    if (filter.nurOffen) { params = params.set('nurOffen', 'true'); }
    if (filter.nurGeaendertSeitPruefung) { params = params.set('nurGeaendertSeitPruefung', 'true'); }
    if (filter.sortierung) { params = params.set('sortierung', filter.sortierung); }

    return params;
  }


  // =========================================================
  // OFFLINE
  // =========================================================

  /** Alle SPS-Listen, Zeilen und prüfbaren Signale eines Projekts in einer Antwort (GET api/sps/projekt/{id}/offline). */
  getOfflinePaket(projektId: number): Observable<SpsOfflinePaket> {
    return this.http.get<SpsOfflinePaket>(`${this.baseUrl}/projekt/${projektId}/offline`);
  }


  // =========================================================
  // FORTSCHRITT
  // =========================================================

  /** SPS-Fortschritt eines Projekts (Reserve zählt als bearbeitet, nicht in der Erfolgsquote). */
  getFortschritt(projektId: number): Observable<SpsFortschritt> {
    const params = new HttpParams().set('projektId', projektId);
    return this.http.get<SpsFortschritt>(`${this.baseUrl}/fortschritt`, { params });
  }

  /**
   * Die Anlagenteile, die bei den Signalen der SPS-Prüfung dieses Projekts
   * tatsächlich vorkommen – Optionen für den Anlagenteil-Filter von
   * `getSignale`, über alle prüfbaren Signale (nicht nur die geladene Seite),
   * alphabetisch und ohne Dubletten. Anlagenteile ohne Signal fehlen absichtlich.
   */
  getAnlagenteile(projektId: number): Observable<SpsAnlagenteilOptionen> {
    const params = new HttpParams().set('projektId', projektId);
    return this.http.get<SpsAnlagenteilOptionen>(`${this.baseUrl}/anlagenteile`, { params });
  }


  // =========================================================
  // SIGNALVERKNÜPFUNG
  // =========================================================

  /** Alle ungeklärten Zuordnungen eines Projekts mit ihren Kandidaten. */
  getZuordnungKandidaten(projektId: number): Observable<SpsUngeklaerteZuordnung[]> {
    const params = new HttpParams().set('projektId', projektId);
    return this.http.get<SpsUngeklaerteZuordnung[]>(`${this.baseUrl}/zuordnung/kandidaten`, { params });
  }

  /**
   * Eintrag manuell einem Zielsignal zuordnen. Sind beide Signale geprüft,
   * muss `konfliktloesung` gesetzt sein – sonst 409 (siehe `istVersionskonflikt`).
   */
  zuordnungBestaetigen(projektId: number, request: SpsZuordnungBestaetigenRequest): Observable<SpsSignal> {
    const params = new HttpParams().set('projektId', projektId);
    return this.http.post<SpsSignal>(`${this.baseUrl}/zuordnung/bestaetigen`, request, { params });
  }

  /** Ungeklärte Quelle als eigenständiges Signal bestätigen. */
  zuordnungEigenstaendig(projektId: number, request: SpsZuordnungEigenstaendigRequest): Observable<SpsSignal> {
    const params = new HttpParams().set('projektId', projektId);
    return this.http.post<SpsSignal>(`${this.baseUrl}/zuordnung/eigenstaendig`, request, { params });
  }

  /** Eindeutige ungeklärte Zuordnungen automatisch klären (läuft über die Bereinigung, nur sichere Fälle). */
  zuordnungAutomatisch(projektId: number): Observable<SpsZuordnungAutomatischErgebnis> {
    const params = new HttpParams().set('projektId', projektId);
    return this.http.post<SpsZuordnungAutomatischErgebnis>(`${this.baseUrl}/zuordnung/automatisch`, null, { params });
  }


  // =========================================================
  // BEREINIGUNG DOPPELTER SIGNALE
  // =========================================================

  /** Dry-Run: Bericht über sichere Zusammenführungen, verwaiste Signale und Konflikte. Ändert nichts. */
  getBereinigungBericht(projektId: number): Observable<SpsBereinigungBericht> {
    const params = new HttpParams().set('projektId', projektId);
    return this.http.get<SpsBereinigungBericht>(`${this.baseUrl}/bereinigung/bericht`, { params });
  }

  /** Wendet nur die sicheren Teile des Berichts an (ausdrücklich bestätigt). Wiederholbar. */
  bereinigungAnwenden(projektId: number): Observable<SpsBereinigungErgebnis> {
    const params = new HttpParams().set('projektId', projektId).set('bestaetigen', 'true');
    return this.http.post<SpsBereinigungErgebnis>(`${this.baseUrl}/bereinigung/anwenden`, null, { params });
  }

  /**
   * Bestandsbereinigung (Dry-Run) über ALLE Projekte mit SPS-Daten – für Listen,
   * die vor der automatischen Zusammenführung importiert wurden. Nur SuperAdmin. Ändert nichts.
   */
  getAltbestandBericht(): Observable<SpsAltbestandBericht> {
    return this.http.get<SpsAltbestandBericht>(`${this.baseUrl}/bereinigung/altbestand`);
  }

  /** Wendet die sicheren Teile der Bestandsbereinigung für alle Projekte an (ausdrücklich bestätigt, nur SuperAdmin). */
  altbestandAnwenden(): Observable<SpsAltbestandErgebnis> {
    const params = new HttpParams().set('bestaetigen', 'true');
    return this.http.post<SpsAltbestandErgebnis>(`${this.baseUrl}/bereinigung/altbestand/anwenden`, null, { params });
  }

  /** Manuelle Entscheidung: ganze Signale zusammenführen; bei zwei geprüften Signalen ist `konfliktloesung` nötig (sonst 409). */
  signaleZusammenfuehren(projektId: number, request: SpsSignaleZusammenfuehrenRequest): Observable<SpsSignal> {
    const params = new HttpParams().set('projektId', projektId);
    return this.http.post<SpsSignal>(`${this.baseUrl}/signale/zusammenfuehren`, request, { params });
  }

  /** Manuelle Entscheidung: Signal bleibt trotz gleicher Adresse eigenständig. */
  signalEigenstaendig(projektId: number, signalId: number): Observable<SpsSignal> {
    const params = new HttpParams().set('projektId', projektId);
    return this.http.post<SpsSignal>(`${this.baseUrl}/signale/${signalId}/eigenstaendig`, null, { params });
  }


  // =========================================================
  // FEHLERHILFEN
  // =========================================================

  /** HTTP 409: Versions- oder Zusammenführungskonflikt – Komponente sollte neu laden bzw. nachfragen. */
  static istVersionskonflikt(fehler: unknown): fehler is HttpErrorResponse {
    return fehler instanceof HttpErrorResponse && fehler.status === 409;
  }

  /** Lesbare Fehlermeldung aus einer Backend-Antwort (`{ message }`) oder ein Ersatztext. */
  static fehlertext(fehler: unknown, ersatz = 'Die Anfrage konnte nicht ausgeführt werden.'): string {
    if (fehler instanceof HttpErrorResponse) {
      const body = fehler.error as { message?: string } | string | null;

      if (body && typeof body === 'object' && typeof body.message === 'string') {
        return body.message;
      }

      if (typeof body === 'string' && body.length > 0) {
        return body;
      }
    }

    return ersatz;
  }
}
